import { logger, createLogger } from '@/utils/logger'
import { StorageService } from './storage.service'

const fileLogger = createLogger('device.service')

/**
 * Service for managing device ID
 * 
 * Device ID is a unique identifier for each device installation.
 * It's used for device-aware conflict resolution in sync operations.
 */
export class DeviceService {
  private static readonly STORAGE_KEY = 'device_id'
  private deviceId: string | null = null
  private storage: StorageService

  constructor(storage: StorageService) {
    this.storage = storage
  }

  /**
   * Generate a unique device ID
   * Format: web-{uuid}
   */
  private generateDeviceId(): string {
    // Generate UUID v4
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
    
    return `web-${uuid}`
  }

  /**
   * Get or create device ID
   * 
   * If device ID doesn't exist, generates a new one and stores it.
   * Device ID is persistent across app restarts.
   * 
   * @returns Device ID string
   */
  async getDeviceId(): Promise<string> {
    // Return cached value if available
    if (this.deviceId) {
      return this.deviceId
    }

    try {
      // Try to load from storage
      const storedId = await this.storage.get<string>(DeviceService.STORAGE_KEY)
      
      if (storedId && typeof storedId === 'string' && storedId.length > 0) {
        this.deviceId = storedId
        fileLogger.log(`Loaded existing device ID: ${this.deviceId.substring(0, 20)}...`)
        return this.deviceId
      }

      // Generate new device ID
      this.deviceId = this.generateDeviceId()
      
      // Store it
      await this.storage.set(DeviceService.STORAGE_KEY, this.deviceId)
      fileLogger.log(`Generated new device ID: ${this.deviceId.substring(0, 20)}...`)
      
      return this.deviceId
    } catch (error) {
      fileLogger.error('Failed to get device ID:', error)
      
      // Fallback: generate temporary ID (won't persist)
      this.deviceId = this.generateDeviceId()
      fileLogger.warn('Using temporary device ID (not persisted)')
      return this.deviceId
    }
  }

  /**
   * Clear device ID (for testing or reset purposes)
   */
  async clearDeviceId(): Promise<void> {
    try {
      await this.storage.remove(DeviceService.STORAGE_KEY)
      this.deviceId = null
      fileLogger.log('Device ID cleared')
    } catch (error) {
      fileLogger.error('Failed to clear device ID:', error)
    }
  }

  /**
   * Initialize device ID (call on app startup)
   */
  async initialize(): Promise<void> {
    try {
      await this.getDeviceId()
      fileLogger.log('Device service initialized')
    } catch (error) {
      fileLogger.error('Failed to initialize device service:', error)
    }
  }
}




